import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DesktopShell from "@/components/DesktopShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Closet from "@/pages/Closet";
import Chat from "@/pages/Chat";
import OutfitResult from "@/pages/OutfitResult";
import StyleProfile from "@/pages/StyleProfile";
import EventsList from "@/pages/EventsList";
import EventNew from "@/pages/EventNew";
import EventDetail from "@/pages/EventDetail";
import ConsentGate from "@/pages/ConsentGate";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Refunds from "@/pages/legal/Refunds";
import Contact from "@/pages/legal/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminVerifyEmail from "@/pages/admin/AdminVerifyEmail";
import { Toaster } from "sonner";

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public legal */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/contact" element={<Contact />} />

      {/* Admin (separate auth) */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route path="/admin/verify-email" element={<AdminVerifyEmail />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/settings" element={<AdminSettings />} />

      {/* User consent gate (must be signed in but not fully onboarded yet) */}
      <Route path="/consent" element={<ProtectedRoute requireOnboarded={false} requireConsent={false}><ConsentGate /></ProtectedRoute>} />

      {/* User app */}
      <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/closet" element={<ProtectedRoute><Closet /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/outfit" element={<ProtectedRoute><OutfitResult /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><StyleProfile /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsList /></ProtectedRoute>} />
      <Route path="/events/new" element={<ProtectedRoute><EventNew /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ConditionalDesktopShell />
        <Router />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

// DesktopShell is a decorative magazine backdrop sized for the mobile column
// (max-w-md). Pages that use a wider content column (legal, admin) would overlap
// its side panels, so we hide it there.
function ConditionalDesktopShell() {
  const location = useLocation();
  const path = location.pathname || "";
  const HIDE_PREFIXES = ["/admin", "/privacy", "/terms", "/refunds", "/contact"];
  if (HIDE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  return <DesktopShell />;
}
